import {ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import { ReplaySubject, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import {PropertyService} from '../services/property.service';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {CommonModule, DatePipe, KeyValuePipe} from '@angular/common';
import {NzDividerModule} from 'ng-zorro-antd/divider';
import {BaseChartDirective, NgChartsModule} from 'ng2-charts';
import {ChartConfiguration} from 'chart.js';
import {NzColDirective, NzRowDirective} from 'ng-zorro-antd/grid';
import {NzStatisticModule} from 'ng-zorro-antd/statistic';
import {NzTableModule} from 'ng-zorro-antd/table';
import {FormsModule} from '@angular/forms';
import {NzModalModule} from 'ng-zorro-antd/modal';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzListModule} from 'ng-zorro-antd/list';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzToolTipModule} from 'ng-zorro-antd/tooltip';
import {Router} from '@angular/router';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import {NzTabsModule} from 'ng-zorro-antd/tabs';
import {NzInputModule} from 'ng-zorro-antd/input';
import { UserTourService } from '../services/user-tour.service';
import { TourActionRegistryService } from '../services/tour-action-registry.service';
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    NzSpinModule,
    NzCardModule,
    NzTagModule,
    TranslateModule,
    DatePipe,
    NzDividerModule,
    KeyValuePipe,
    CommonModule,
    NgChartsModule,
    NzRowDirective,
    NzColDirective,
    NzStatisticModule,
    NzTableModule,
    FormsModule,
    NzModalModule,
    NzButtonModule,
    NzInputModule,
    NzListModule,
    NzIconModule,
    NzToolTipModule,
    NzAvatarModule,
    NzProgressModule,
    NzBadgeModule,
    NzTabsModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.less'
})
export class DashboardComponent implements OnInit, OnDestroy {
  activeTabIndex = 0; // 0=Overview, 1=Notifications, 2=Properties

  /** UI helpers */
  lastLoadedAt: Date | null = null;
  propertySearch = '';

  // --- Notifications tab: subscriber properties table (server-side) ---
  notifLoading = false;
  notifSearch = '';
  notifSearchDebounceTimer: any = null;

  notifPageIndex = 1;
  notifPageSize = 10;
  notifTotal = 0;

  notifSortField: 'address' | 'pushSubscribers' = 'pushSubscribers';
  notifSortOrder: 'ascend' | 'descend' = 'descend';

  subscriberPropertiesPage: Array<{ propertyId: string; address: string; pushSubscribers: number }> = [];

  propertiesFilter: 'all' | 'outdated' | 'inactive' = 'all';

  @ViewChild('propertiesAnchor') propertiesAnchor!: ElementRef<HTMLElement>;

  loading = true;
  private autoTourStartRequested = false;
  private readonly dashboardReady$ = new ReplaySubject<void>(1);
  private unregisterDashboardTourAction: (() => void) | null = null;
  stats: any[] = [];
  isModalVisible = false;
  selectedStat: any = null;
  selectedLanguageChart: ChartConfiguration<'pie'> | null = null;
  selectedTrendChart: ChartConfiguration<'line'> | null = null;
  selectedInteractionChart: ChartConfiguration<'pie'> | null = null;
  @ViewChildren(BaseChartDirective) charts!: QueryList<BaseChartDirective>;
  languageColorMap: Record<string, string> = {
    ar: '#5470C6',  // Arabic - Blue
    de: '#91CC75',  // German - Green
    en: '#EE6666',  // English - Red
    fr: '#FC8452',  // French - Orange
    pl: '#9A60B4',  // Polish - Purple
    ru: '#3BA272',  // Russian - Teal
    tr: '#FAC858',  // Turkish - Yellow
    uk: '#73C0DE'   // Ukrainian - Light Blue
  };
  interactionColorMap: Record<string, string> = {
    'document-view': '#5470C6',    // Blue
    'section-view': '#91CC75',     // Green
    'language-change': '#EE6666',  // Red
    'emergency-call': '#FC8452',   // Orange
    'emergency-instruction-view': '#40A9FF', // Light Blue
    'contact-click': '#9A60B4',     // Purple
    'announcement-view': '#2DB7F5', // Cyan
    'announcement-attachment-view': '#13C2C2', // Teal
    'link-click': '#D48265',        // Brown/Orange
    'push-subscribed': '#00A854',   // Green/Teal
    'push-unsubscribed': '#8B0000',  // Dark Red
    'hospitality-feedback': '#3CB371', // MediumSeaGreen
    'copy': '#708090',                  // SlateGray
    'rating-submission': '#FFD700',    // Gold
  };

  // ---- Company insights (aggregated across all properties) ----
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  dows: number[] = [1, 2, 3, 4, 5, 6, 0];

  companyHeatmap: number[][] = [];   // [dow][hour] => count
  companyHeatmapMax = 0;

  topLinks: Array<{
    url: string;
    label: string;
    clicks: number;
    propertyCounts: Array<{ address: string; count: number }>;
  }> = [];

  contactSummary: {
    phone: number;
    email: number;
    whatsapp: number;
    other: number;
    topPhones: Array<{
      value: string;
      kind: 'phone' | 'mobile';
      relatedTo?: string;
      relatedName?: string;
      count: number;
      properties: string[];
    }>;
    topEmails: Array<{
      value: string;
      relatedTo?: string;
      relatedName?: string;
      count: number;
      properties: string[];
    }>;
    topWhatsApp: Array<{
      value: string;
      relatedTo?: string;
      relatedName?: string;
      count: number;
      properties: string[];
    }>;
  } = { phone: 0, email: 0, whatsapp: 0, other: 0, topPhones: [], topEmails: [], topWhatsApp: [] };

  topDocuments: Array<{
    fileName: string;
    views: number;
    propertyCounts: Array<{ address: string; count: number }>;
  }> = [];

  topAnnouncements: Array<{
    announcementId: string;
    title: string;
    views: number;
    propertyCounts: Array<{ address: string; count: number }>;
  }> = [];

  sortByAddress = (a: any, b: any): number =>
    a.address.localeCompare(b.address, this.translate.currentLang);

  sortByTotalScans = (a: any, b: any): number =>
    a.totalScans - b.totalScans;

  sortByUniqueVisitors = (a: any, b: any): number =>
    a.uniqueVisitors - b.uniqueVisitors;

  sortByLastUpdated = (a: any, b: any): number =>
    new Date(a.lastUpdate).getTime() - new Date(b.lastUpdate).getTime();

  sortByPushSubscribers = (a: any, b: any): number =>
    (a?.pushSubscribers || 0) - (b?.pushSubscribers || 0);

  // --- helpers for sorting "Properties" column in insight tables ---
  private totalPropertyCount(items: Array<{ address: string; count: number }> = []): number {
    return (items || []).reduce((acc, x) => acc + (x?.count || 0), 0);
  }

  // --- Insights table sort fns: Top Links ---
  sortByLinkLabel = (a: any, b: any): number =>
    (a?.label || '').localeCompare((b?.label || ''), this.translate.currentLang);

  sortByLinkClicks = (a: any, b: any): number =>
    (a?.clicks || 0) - (b?.clicks || 0);

  sortByLinkProperties = (a: any, b: any): number =>
    this.totalPropertyCount(a?.propertyCounts) - this.totalPropertyCount(b?.propertyCounts);

  // --- Insights table sort fns: Top Documents ---
  sortByDocName = (a: any, b: any): number =>
    (a?.fileName || '').localeCompare((b?.fileName || ''), this.translate.currentLang);

  sortByDocViews = (a: any, b: any): number =>
    (a?.views || 0) - (b?.views || 0);

  sortByDocProperties = (a: any, b: any): number =>
    this.totalPropertyCount(a?.propertyCounts) - this.totalPropertyCount(b?.propertyCounts);

  // --- Insights table sort fns: Top Announcements ---
  sortByAnnouncementTitle = (a: any, b: any): number =>
    (a?.title || '').localeCompare((b?.title || ''), this.translate.currentLang);

  sortByAnnouncementViews = (a: any, b: any): number =>
    (a?.views || 0) - (b?.views || 0);

  sortByAnnouncementProperties = (a: any, b: any): number =>
    this.totalPropertyCount(a?.propertyCounts) - this.totalPropertyCount(b?.propertyCounts);

  constructor(private propertyService: PropertyService,
              private cdr: ChangeDetectorRef,
              private translate: TranslateService,
              private router: Router,
              private userTourService: UserTourService,
              private tourActions: TourActionRegistryService,
              private ngZone: NgZone) {}

  ngOnInit(): void {
    this.registerTourActions();
    this.propertyService.getCompanyScanStats().subscribe({
      next: (data) => {
        this.stats = data;
        this.computeCompanyInsights();
        this.lastLoadedAt = new Date();
        this.completeInitialDashboardLoad();
      },
      error: () => {
        this.completeInitialDashboardLoad();
      }
    });
  }

  ngOnDestroy(): void {
    this.unregisterDashboardTourAction?.();
    this.unregisterDashboardTourAction = null;
    this.dashboardReady$.complete();
  }

  private registerTourActions(): void {
    this.unregisterDashboardTourAction = this.tourActions.register(
      'dashboard.ensure-workspace-ready',
      async () => {
        await firstValueFrom(this.dashboardReady$.pipe(take(1)));
        return { close: () => undefined };
      }
    );
  }

  private completeInitialDashboardLoad(): void {
    this.loading = false;
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.dashboardReady$.next();
      this.dashboardReady$.complete();
      this.startAutoTourAfterDashboardReady();
    });
  }

  private startAutoTourAfterDashboardReady(): void {
    if (this.autoTourStartRequested) {
      return;
    }
    this.autoTourStartRequested = true;
    void this.userTourService.startAutoTour();
  }

  objectKeys = Object.keys;

  getStatusColor(isOutdated: boolean, isInactive: boolean): string {
    if (isOutdated && isInactive) return 'volcano';
    if (isOutdated) return 'orange';
    if (isInactive) return 'geekblue';
    return 'green';
  }

  getLanguageChartData(languages: Record<string, number>): ChartConfiguration<'pie'> {
    const labels = Object.keys(languages || {}).map(l => l.toUpperCase());
    const data = Object.values(languages || {});
    const translatedLabel = this.translate.instant('DASHBOARD.LANGUAGE_BREAKDOWN');
    const backgroundColor = labels.map(lang => this.languageColorMap[lang.toLowerCase()] || '#CCCCCC');

    return {
      type: 'pie',
      data: {
        labels,
        datasets: [{
          data,
          label: translatedLabel,
          backgroundColor
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'right' } }
      }
    };
  }

  getTrendChartData(dailyScans: { date: string, count: number }[]): ChartConfiguration<'line'> {
    const labels = dailyScans.map(d =>
      new Date(d.date).toLocaleDateString(this.translate.currentLang)
    );
    const counts = dailyScans.map(d => d.count);
    const translatedLabel = this.translate.instant('DASHBOARD.TOTAL_SCANS');

    return {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data: counts,
          label: translatedLabel,
          tension: 0.4,
          fill: false,
          borderColor: '#5470C6',
          backgroundColor: '#5470C6'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: this.translate.instant('DASHBOARD.LAST_UPDATED')
            },
            ticks: { maxRotation: 45, minRotation: 45 }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: translatedLabel
            }
          }
        }
      }
    };
  }

  get totalScans(): number {
    return this.stats.reduce((acc, s) => acc + s.totalScans, 0);
  }

  get uniqueVisitors(): number {
    return this.stats.reduce((acc, s) => acc + s.uniqueVisitors, 0);
  }

  get outdatedCount(): number {
    return this.stats.filter(s => s.isOutdated).length;
  }

  get inactiveCount(): number {
    return this.stats.filter(s => s.isInactive).length;
  }
  // ---- Notification KPI getters ----
  get totalPushSubscribers(): number {
    return this.stats.reduce((acc, s) => acc + (s.pushSubscribers || 0), 0);
  }

  get totalPushUnsubscribes(): number {
    // prefer the dedicated field; fallback to interactions map if needed
    return this.stats.reduce(
      (acc, s) => acc + (s.pushUnsubscribes ?? (s.interactions?.['push-unsubscribed'] || 0)),
      0
    );
  }

  get totalPushSubscribeEvents(): number {
    return this.stats.reduce(
      (acc, s) => acc + (s.pushSubscribes ?? (s.interactions?.['push-subscribed'] || 0)),
      0
    );
  }

  /** Simple overall opt-in rate = current subscribers / unique visitors (all time) */
  get pushOptInRate(): number {
    const uv = this.uniqueVisitors || 0;
    if (!uv) return 0;
    return Math.round(((this.totalPushSubscribers / uv) * 100) * 10) / 10; // one decimal
  }

  topSubscriberProperties(limit = 5): any[] {
    return [...this.stats]
      .sort((a, b) => (b.pushSubscribers || 0) - (a.pushSubscribers || 0))
      .slice(0, limit);
  }

  get maxPushSubscribers(): number {
    return Math.max(0, ...this.stats.map(s => s.pushSubscribers || 0));
  }

  get filteredStats(): any[] {
    let base: any[] = this.stats;

    if (this.propertiesFilter === 'outdated') {
      base = base.filter(s => !!s.isOutdated);
    } else if (this.propertiesFilter === 'inactive') {
      base = base.filter(s => !!s.isInactive);
    }

    const term = (this.propertySearch || '').trim().toLowerCase();
    if (term) {
      base = base.filter(s => (s?.address || '').toString().toLowerCase().includes(term));
    }

    return base;
  }

  showOutdatedProperties(): void {
    this.propertiesFilter = 'outdated';
    this.activeTabIndex = 2;

    setTimeout(() => {
      this.propertiesAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  showInactiveProperties(): void {
    this.propertiesFilter = 'inactive';
    this.activeTabIndex = 2;

    setTimeout(() => {
      this.propertiesAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  clearPropertiesFilter(): void {
    this.propertiesFilter = 'all';
  }

  percentOfMax(value: number): number {
    if (!this.maxPushSubscribers) return 0;
    return Math.round((value / this.maxPushSubscribers) * 100);
  }

  openDetailsModal(stat: any): void {
    this.selectedStat = stat;
    this.syncSelectedStatCharts();
    this.cdr.detectChanges();
    this.isModalVisible = true;
  }

  closeModal(): void {
    this.selectedStat = null;
    this.selectedLanguageChart = null;
    this.selectedTrendChart = null;
    this.selectedInteractionChart = null;
    this.isModalVisible = false;
  }

  private syncSelectedStatCharts(): void {
    const stat = this.selectedStat;

    this.selectedLanguageChart = stat?.languages && this.objectKeys(stat.languages).length > 0
      ? this.getLanguageChartData(stat.languages)
      : null;

    this.selectedTrendChart = stat
      ? this.getTrendChartData(stat.dailyScans || [])
      : null;

    this.selectedInteractionChart = stat?.interactions && this.interactionEntries(stat.interactions).length > 0
      ? this.getInteractionChartData(stat.interactions)
      : null;
  }

  getDaysSince(date: string | Date): number {
    const then = new Date(date).getTime();
    const now = Date.now();
    const diff = now - then;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  getUpdateBadgeText(stat: any): string {
    const days = this.getDaysSince(stat.lastUpdate);
    return this.translate.instant('DASHBOARD.UPDATED_X_DAYS_AGO', { days });
  }

  getScanBadgeText(stat: any): string {
    if (!stat.dailyScans.length) return this.translate.instant('DASHBOARD.NO_SCANS');

    const mostRecent = stat.dailyScans
      .map((s: any) => new Date(s.date))
      .sort((a: Date, b: Date) => b.getTime() - a.getTime())[0];

    const days = this.getDaysSince(mostRecent);
    return this.translate.instant('DASHBOARD.LAST_SCAN_X_DAYS_AGO', { days });
  }

  formatDateByLang(dateStr: string | Date): string {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return date.toLocaleDateString(this.translate.currentLang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  interactionEntries(interactions: Record<string, number> | null | undefined) {
    if (!interactions) return [];
    return Object.entries(interactions).map(([key, value]) => ({ key, value }));
  }

  getInteractionChartData(interactions: Record<string, number>): ChartConfiguration<'pie'> {
    const typeKeys = Object.keys(interactions || {});
    const data = Object.values(interactions || {});
    const backgroundColor = typeKeys.map(key => this.interactionColorMap[key] || '#CCCCCC');

    const labelMap: Record<string, string> = {
      'document-view': this.translate.instant('DASHBOARD.DOCUMENT_VIEW'),
      'section-view': this.translate.instant('DASHBOARD.SECTION_VIEW'),
      'language-change': this.translate.instant('DASHBOARD.LANGUAGE_CHANGE'),
      'emergency-call': this.translate.instant('DASHBOARD.EMERGENCY_CALL'),
      'emergency-instruction-view': this.translate.instant('DASHBOARD.EMERGENCY_INSTRUCTION_VIEW'),
      'contact-click': this.translate.instant('DASHBOARD.CONTACT_CLICK'),
      'announcement-view': this.translate.instant('DASHBOARD.ANNOUNCEMENT_VIEW'),
      'announcement-attachment-view': this.translate.instant('DASHBOARD.ANNOUNCEMENT_ATTACHMENT_VIEW'),
      'link-click': this.translate.instant('DASHBOARD.LINK_CLICK'),
      'push-subscribed': this.translate.instant('DASHBOARD.PUSH_SUBSCRIBED'),
      'push-unsubscribed': this.translate.instant('DASHBOARD.PUSH_UNSUBSCRIBED'),
      'hospitality-feedback': this.translate.instant('DASHBOARD.HOSPITALITY_FEEDBACK'),
      'copy': this.translate.instant('DASHBOARD.COPY'),
      'rating-submission': this.translate.instant('DASHBOARD.RATING_SUBMISSION'),
    };

    const labels = typeKeys.map(key => labelMap[key] || key);

    return {
      type: 'pie',
      data: {
        labels,
        datasets: [{
          data,
          label: this.translate.instant('DASHBOARD.INTERACTION_TYPES'),
          backgroundColor
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right' },
          tooltip: {
            callbacks: {
              label: (tooltipItem: any) => {
                const label = labels[tooltipItem.dataIndex];
                const value = data[tooltipItem.dataIndex];
                return `${label}: ${value}`;
              }
            }
          }
        }
      }
    };
  }

  translateInteractionType(type: string): string {
    const translations: Record<string, string> = {
      'document-view': this.translate.instant('DASHBOARD.DOCUMENT_VIEW'),
      'section-view': this.translate.instant('DASHBOARD.SECTION_VIEW'),
      'language-change': this.translate.instant('DASHBOARD.LANGUAGE_CHANGE'),
      'emergency-call': this.translate.instant('DASHBOARD.EMERGENCY_CALL'),
      'emergency-instruction-view': this.translate.instant('DASHBOARD.EMERGENCY_INSTRUCTION_VIEW'),
      'contact-click': this.translate.instant('DASHBOARD.CONTACT_CLICK'),
      'announcement-view': this.translate.instant('DASHBOARD.ANNOUNCEMENT_VIEW'),
      'announcement-attachment-view': this.translate.instant('DASHBOARD.ANNOUNCEMENT_ATTACHMENT_VIEW'),
      'link-click': this.translate.instant('DASHBOARD.LINK_CLICK'),
      'push-subscribed': this.translate.instant('DASHBOARD.PUSH_SUBSCRIBED'),
      'push-unsubscribed': this.translate.instant('DASHBOARD.PUSH_UNSUBSCRIBED'),
      'hospitality-feedback': this.translate.instant('DASHBOARD.HOSPITALITY_FEEDBACK'),
      'copy': this.translate.instant('DASHBOARD.COPY'),
      'rating-submission': this.translate.instant('DASHBOARD.RATING_SUBMISSION'),
    };
    return translations[type] || type;
  }

  private tSafe(key: string, fallback: string, params?: Record<string, any>): string {
    const val = this.translate.instant(key, params);
    return val && val !== key ? val : fallback;
  }

  formatContactRelatedTo(relatedTo?: string | null): string {
    const normalized = String(relatedTo || '').trim().toLowerCase() || 'unknown';
    return this.tSafe(`DASHBOARD.CONTACT_RELATED_TO.${normalized}`, this.tSafe('DASHBOARD.CONTACT_RELATED_TO.unknown', 'Other'));
  }

  formatInteractionDescription(item: any): string {
    const meta = item?.metadata || {};

    // Common translated labels (fallbacks are English)
    const L_SECTION      = this.tSafe('DASHBOARD.INTERACTION.SECTION', 'Abschnitt');
    const L_FILE         = this.tSafe('DASHBOARD.INTERACTION.FILE', 'Datei');
    const L_LANGUAGE     = this.tSafe('DASHBOARD.INTERACTION.LANGUAGE', 'Sprache');
    const L_INSTRUCTION  = this.tSafe('DASHBOARD.INTERACTION.INSTRUCTION', 'Anleitung');
    const L_ANNOUNCEMENT = this.tSafe('DASHBOARD.INTERACTION.ANNOUNCEMENT', 'Ankündigung');
    const L_LINK         = this.tSafe('DASHBOARD.INTERACTION.LINK', 'Link');
    const L_ID           = this.tSafe('DASHBOARD.INTERACTION.ID', 'ID');
    const L_NA           = this.tSafe('DASHBOARD.NA', 'k. A.');
    const L_UNKNOWN      = this.tSafe('DASHBOARD.INTERACTION.UNKNOWN', 'Unbekannte Interaktion');

    switch (item?.type) {
      case 'document-view': {
        const sectionPart = meta.sectionTitle
          ? `${L_SECTION}: ${meta.sectionTitle}, `
          : '';
        const fileName = meta.fileName || L_NA;
        return `${sectionPart}${L_FILE}: ${fileName}`;
      }

      case 'section-view':
        return `${L_SECTION}: ${meta.sectionTitle || L_NA}`;

      case 'contact-click': {
        const kind = (meta.kind || '').toString().toLowerCase();

        const label =
          kind === 'mobile'
            ? this.tSafe('DASHBOARD.CONTACT_MOBILE', 'Mobile')
            : kind === 'phone'
              ? this.tSafe('DASHBOARD.CONTACT_PHONE', 'Phone')
              : kind === 'whatsapp'
                ? this.tSafe('DASHBOARD.CONTACT_WHATSAPP', 'WhatsApp')
              : this.tSafe('DASHBOARD.CONTACT_EMAIL', 'Email');

        const value = meta.value || L_NA;

        const relatedTo = meta.relatedTo ? meta.relatedTo.toString() : '';
        const relatedName = meta.relatedName ? meta.relatedName.toString() : '';

        if (relatedTo || relatedName) {
          const relLabel = this.formatContactRelatedTo(relatedTo);
          const relNamePart = relatedName ? ` — ${relatedName}` : '';
          return `${label}: ${value} • ${relLabel}${relNamePart}`;
        }

        return `${label}: ${value}`;
      }

      case 'language-change':
        return `${L_LANGUAGE}: ${meta.language?.toUpperCase() || L_NA}`;

      case 'push-subscribed':
        return `${L_LANGUAGE}: ${meta.language?.toUpperCase() || L_NA}`;

      case 'emergency-call':
        return this.translate.instant('DASHBOARD.EMERGENCY_TRIGGERED');

      case 'emergency-instruction-view':
        return `${L_INSTRUCTION}: ${meta.title || meta.id || L_NA}`;

      case 'announcement-view':
        return `${L_ANNOUNCEMENT}: ${meta.announcementTitle || meta.title || meta.announcementId || L_NA}`;

      case 'announcement-attachment-view': {
        const announcement = meta.announcementTitle || meta.title || meta.announcementId || L_NA;
        const fileName = meta.fileName || L_NA;
        return `${L_ANNOUNCEMENT}: ${announcement}, ${L_FILE}: ${fileName}`;
      }

      case 'link-click': {
        const label = meta.label || meta.url || L_NA;
        const urlPart = meta.url ? ` (${meta.url})` : '';
        return `${L_LINK}: ${label}${urlPart}`;
      }

      case 'push-unsubscribed':
        return this.translate.instant('DASHBOARD.PUSH_UNSUBSCRIBED');

      case 'hospitality-feedback': {
        const action = (meta.action || '').toString().toLowerCase();
        const channel =
          action === 'email'
            ? this.tSafe('DASHBOARD.INTERACTION.CHANNEL_EMAIL', 'E-Mail')
            : action === 'phone'
              ? this.tSafe('DASHBOARD.INTERACTION.CHANNEL_PHONE', 'Telefon')
              : this.tSafe('DASHBOARD.INTERACTION.CHANNEL_FORM', 'Formular');

        const source = meta.source ? ` (${meta.source})` : '';
        const value = meta.value ? `: ${meta.value}` : '';
        const via = this.tSafe('DASHBOARD.INTERACTION.FEEDBACK_VIA', 'Feedback über');
        return `${via} ${channel}${value}${source}`;
      }

      case 'copy': {
        const kind = (meta.kind || 'generic').toString().toLowerCase();

        const label =
          kind === 'wifi-ssid'
            ? this.tSafe('DASHBOARD.INTERACTION.COPY_WIFI_SSID', 'Wi-Fi network')
            : kind === 'wifi-password'
              ? this.tSafe('DASHBOARD.INTERACTION.COPY_WIFI_PASSWORD', 'Wi-Fi password')
              : this.tSafe('DASHBOARD.INTERACTION.COPY_VALUE', 'Value');

        const copied = this.tSafe('DASHBOARD.INTERACTION.COPIED', 'Kopiert');
        return `${copied}: ${label}`;
      }

      case 'rating-submission': {
        const c = meta.cleanliness;
        const f = meta.comfort;
        const m = meta.communication;
        const o = meta.overall ?? meta.score ?? meta.rating;

        const L_OVERALL       = this.tSafe('RATING_OVERVIEW.CATEGORIES.OVERALL',       'Overall');
        const L_CLEANLINESS   = this.tSafe('RATING_OVERVIEW.CATEGORIES.CLEANLINESS',   'Cleanliness');
        const L_COMFORT       = this.tSafe('RATING_OVERVIEW.CATEGORIES.COMFORT',       'Comfort');
        const L_COMMUNICATION = this.tSafe('RATING_OVERVIEW.CATEGORIES.COMMUNICATION', 'Communication');

        const pieces: string[] = [];
        if (o != null) pieces.push(`${L_OVERALL}: ${o}`);
        if (c != null) pieces.push(`${L_CLEANLINESS}: ${c}`);
        if (f != null) pieces.push(`${L_COMFORT}: ${f}`);
        if (m != null) pieces.push(`${L_COMMUNICATION}: ${m}`);

        return pieces.length ? pieces.join(', ') : this.translate.instant('DASHBOARD.RATING_SUBMISSION');
      }

      default:
        return L_UNKNOWN;
    }
  }

  exportStatsToCSV(): void {
    const rows = this.stats.map(stat => {
      const languageSummary = (Object.entries(stat.languages || {}) as [string, number][])
        .map(([lang, count]) => `${lang.toUpperCase()}: ${count}`)
        .join(', ');

      const dailyScanSummary = (stat.dailyScans || [])
        .sort((a: { date: string }, b: { date: string }) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        )
        .slice(0, 5)
        .map((ds: { date: string; count: number }) =>
          `${new Date(ds.date).toLocaleDateString(this.translate.currentLang)}: ${ds.count}`
        )
        .join(', ');

      const hourlyScanSummary = (stat.hourlyScans || [])
        .sort((a: { hour: number }, b: { hour: number }) => a.hour - b.hour)
        .map((hs: { hour: number; count: number }) => `${hs.hour}:00 - ${hs.count}`)
        .join(', ');

      const interactionSummary = (Object.entries(stat.interactions || {}) as [string, number][])
        .map(([type, count]) => `${this.translateInteractionType(type)}: ${count}`)
        .join(', ');

      return {
        [this.translate.instant('DASHBOARD.PROPERTY')]: stat.address,
        [this.translate.instant('DASHBOARD.TOTAL_SCANS')]: stat.totalScans,
        [this.translate.instant('DASHBOARD.UNIQUE_VISITORS')]: stat.uniqueVisitors,
        [this.translate.instant('DASHBOARD.LAST_UPDATED')]: this.formatDateByLang(stat.lastUpdate),
        [this.translate.instant('DASHBOARD.STATUS')]:
          stat.isOutdated
            ? this.translate.instant('DASHBOARD.OUTDATED')
            : stat.isInactive
              ? this.translate.instant('DASHBOARD.INACTIVE')
              : this.translate.instant('DASHBOARD.HEALTHY'),
        [this.translate.instant('DASHBOARD.LANGUAGE_BREAKDOWN')]: languageSummary,
        [this.translate.instant('DASHBOARD.DAILY_SCANS')]: dailyScanSummary,
        [this.translate.instant('DASHBOARD.HOURLY_SCANS')]: hourlyScanSummary,
        [this.translate.instant('DASHBOARD.INTERACTION_TYPES')]: interactionSummary,
        [this.translate.instant('DASHBOARD.NOTIF_TOTAL_SUBSCRIBERS')]: stat.pushSubscribers || 0,
        [this.translate.instant('DASHBOARD.NOTIF_UNSUBSCRIBES')]: (stat.pushUnsubscribes ?? (stat.interactions?.['push-unsubscribed'] || 0)),
        [this.translate.instant('DASHBOARD.NOTIF_SUBSCRIBE_EVENTS')]: (stat.pushSubscribes ?? (stat.interactions?.['push-subscribed'] || 0)),
        [this.translate.instant('DASHBOARD.NOTIF_SUBSCRIBERS_BY_LANG')]:
          Object.entries(stat.pushSubscribersByLang || {})
            .map(([lang, count]) => `${String(lang).toUpperCase()}: ${count}`)
            .join(', ')
      };
    });

    const csvBody = this.convertToCSV(rows);

    // >>> add BOM so Excel correctly recognizes UTF-8 <<<
    const bom = '\uFEFF';
    const blob = new Blob([bom, csvBody], { type: 'text/csv;charset=utf-8;' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `company_scan_statistics_${this.translate.currentLang}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private convertToCSV(data: any[]): string {
    if (!data.length) return '';
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`).join(','));
    return [headers.join(','), ...rows].join('\r\n');
  }

  goToPropertyListWithSearch(address: string): void {
    const searchTerm = encodeURIComponent(address);
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/properties'], { queryParams: { globalTerm: searchTerm } })
    );
    const fullUrl = window.location.origin + url;
    window.open(fullUrl, '_blank');
  }

  private computeCompanyInsights(): void {
    this.buildCompanyHeatmap();
    this.buildTopLinks();
    this.buildContactSummary();
    this.buildTopDocuments();
    this.buildTopAnnouncements();
  }

  private buildCompanyHeatmap(): void {
    const grid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));

    for (const stat of this.stats || []) {
      const map = stat?.dowHourlyScans;
      if (!map) continue;

      for (const dowStr of Object.keys(map)) {
        const dow = Number(dowStr);
        const hoursObj = map[dowStr] || {};
        for (const hourStr of Object.keys(hoursObj)) {
          const hour = Number(hourStr);
          const count = Number(hoursObj[hourStr] || 0);
          if (dow >= 0 && dow <= 6 && hour >= 0 && hour <= 23) {
            grid[dow][hour] += count;
          }
        }
      }
    }

    this.companyHeatmap = grid;

    let max = 0;
    for (const row of grid) for (const v of row) max = Math.max(max, v);
    this.companyHeatmapMax = max;
  }

  weekdayLabel(dow: number): string {
    const keyMap: Record<number, string> = {
      0: 'DASHBOARD.WEEKDAY_SUN',
      1: 'DASHBOARD.WEEKDAY_MON',
      2: 'DASHBOARD.WEEKDAY_TUE',
      3: 'DASHBOARD.WEEKDAY_WED',
      4: 'DASHBOARD.WEEKDAY_THU',
      5: 'DASHBOARD.WEEKDAY_FRI',
      6: 'DASHBOARD.WEEKDAY_SAT',
    };
    return this.translate.instant(keyMap[dow] || 'DASHBOARD.WEEKDAY_MON');
  }

  heatCellStyle(value: number): Record<string, string> {
    if (!this.companyHeatmapMax || value <= 0) return { background: 'transparent' };
    // opacity from 0.08 .. 0.95
    const alpha = Math.min(0.95, 0.08 + (value / this.companyHeatmapMax) * 0.87);
    // Ant-like blue with intensity
    return { background: `rgba(24, 144, 255, ${alpha})` };
  }

  heatCellTooltip(dow: number, hour: number, value: number): string {
    return `${this.weekdayLabel(dow)} • ${hour}:00 — ${value}`;
  }

  private buildTopLinks(limit = 200): void {
    const map = new Map<string, { url: string; label: string; clicks: number; byProperty: Map<string, number> }>();

    for (const stat of this.stats || []) {
      const address = stat?.address || '—';
      const items = stat?.interactionsDetailed || [];
      for (const it of items) {
        if (it?.type !== 'link-click') continue;
        const url = (it?.metadata?.url || '').toString().trim();
        if (!url) continue;
        const label = (it?.metadata?.label || url).toString().trim();

        if (!map.has(url)) {
          map.set(url, { url, label, clicks: 0, byProperty: new Map<string, number>() });
        }
        const entry = map.get(url)!;
        entry.clicks += 1;
        entry.byProperty.set(address, (entry.byProperty.get(address) || 0) + 1);
        // keep the “best” label we have
        if (entry.label === entry.url && label !== url) entry.label = label;
      }
    }

    this.topLinks = [...map.values()]
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, limit)
      .map(e => ({
        url: e.url,
        label: e.label,
        clicks: e.clicks,
        propertyCounts: [...e.byProperty.entries()]
          .map(([address, count]) => ({ address, count }))
          .sort((a, b) => b.count - a.count)
      }));
  }

  private pickBestRelation(relCounts: Map<string, number>): { relatedTo?: string; relatedName?: string } | undefined {
    // relationKey format: `${relatedTo || ''}|${relatedName || ''}`
    // empty relation is '|'
    let bestKey = '';
    let bestCount = 0;

    for (const [k, c] of relCounts.entries()) {
      if (k === '|') continue; // ignore "no relation"
      if (c > bestCount) {
        bestCount = c;
        bestKey = k;
      }
    }

    if (!bestKey) return undefined;

    const [relatedTo, relatedName] = bestKey.split('|');
    return {
      relatedTo: relatedTo || undefined,
      relatedName: relatedName || undefined,
    };
  }

  private buildContactSummary(): void {
    let phone = 0;
    let email = 0;
    let whatsapp = 0;
    let other = 0;

    const phones = new Map<string, {
      value: string;
      phoneCount: number;
      mobileCount: number;
      properties: Set<string>;
      relationCounts: Map<string, number>; // key = `${relatedTo}|${relatedName}`
    }>();

    const emails = new Map<string, {
      value: string;
      count: number;
      properties: Set<string>;
      relationCounts: Map<string, number>;
    }>();

    const whatsAppNumbers = new Map<string, {
      value: string;
      count: number;
      properties: Set<string>;
      relationCounts: Map<string, number>;
    }>();

    for (const stat of this.stats || []) {
      const address = stat?.address || '—';
      const items = stat?.interactionsDetailed || [];

      for (const it of items) {
        if (it?.type !== 'contact-click') continue;

        const meta = it?.metadata || {};
        const kind = (meta?.kind || '').toString().toLowerCase();
        const value = (meta?.value || '').toString().trim();
        const relatedTo = meta?.relatedTo ? meta.relatedTo.toString() : '';
        const relatedName = meta?.relatedName ? meta.relatedName.toString() : '';
        const relKey = `${relatedTo}|${relatedName}`;

        if (!value) continue;

        if (kind === 'phone' || kind === 'mobile') {
          phone += 1;

          if (!phones.has(value)) {
            phones.set(value, {
              value,
              phoneCount: 0,
              mobileCount: 0,
              properties: new Set<string>(),
              relationCounts: new Map<string, number>(),
            });
          }

          const entry = phones.get(value)!;
          if (kind === 'mobile') entry.mobileCount += 1;
          else entry.phoneCount += 1;

          entry.properties.add(address);
          entry.relationCounts.set(relKey, (entry.relationCounts.get(relKey) || 0) + 1);
          continue;
        }

        if (kind === 'email') {
          email += 1;

          if (!emails.has(value)) {
            emails.set(value, {
              value,
              count: 0,
              properties: new Set<string>(),
              relationCounts: new Map<string, number>(),
            });
          }

          const entry = emails.get(value)!;
          entry.count += 1;
          entry.properties.add(address);
          entry.relationCounts.set(relKey, (entry.relationCounts.get(relKey) || 0) + 1);
          continue;
        }

        if (kind === 'whatsapp') {
          whatsapp += 1;

          if (!whatsAppNumbers.has(value)) {
            whatsAppNumbers.set(value, {
              value,
              count: 0,
              properties: new Set<string>(),
              relationCounts: new Map<string, number>(),
            });
          }

          const entry = whatsAppNumbers.get(value)!;
          entry.count += 1;
          entry.properties.add(address);
          entry.relationCounts.set(relKey, (entry.relationCounts.get(relKey) || 0) + 1);
          continue;
        }

        other += 1;
      }
    }

    const topPhones = [...phones.values()]
      .map(v => {
        const best = this.pickBestRelation(v.relationCounts);
        const kind: 'phone' | 'mobile' = v.mobileCount > v.phoneCount ? 'mobile' : 'phone';

        return {
          value: v.value,
          kind,
          relatedTo: best?.relatedTo,
          relatedName: best?.relatedName,
          count: v.phoneCount + v.mobileCount,
          properties: [...v.properties],
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const topEmails = [...emails.values()]
      .map(v => {
        const best = this.pickBestRelation(v.relationCounts);
        return {
          value: v.value,
          relatedTo: best?.relatedTo,
          relatedName: best?.relatedName,
          count: v.count,
          properties: [...v.properties],
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const topWhatsApp = [...whatsAppNumbers.values()]
      .map(v => {
        const best = this.pickBestRelation(v.relationCounts);
        return {
          value: v.value,
          relatedTo: best?.relatedTo,
          relatedName: best?.relatedName,
          count: v.count,
          properties: [...v.properties],
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    this.contactSummary = { phone, email, whatsapp, other, topPhones, topEmails, topWhatsApp };
  }

  private buildTopDocuments(limit = 200): void {
    const map = new Map<string, { fileName: string; views: number; byProperty: Map<string, number> }>();

    for (const stat of this.stats || []) {
      const address = stat?.address || '—';
      const items = stat?.interactionsDetailed || [];
      for (const it of items) {
        if (it?.type !== 'document-view' && it?.type !== 'announcement-attachment-view') continue;

        const fileName = (it?.metadata?.fileName || '').toString().trim();
        if (!fileName) continue;

        if (!map.has(fileName)) {
          map.set(fileName, { fileName, views: 0, byProperty: new Map<string, number>() });
        }
        const entry = map.get(fileName)!;
        entry.views += 1;
        entry.byProperty.set(address, (entry.byProperty.get(address) || 0) + 1);
      }
    }

    this.topDocuments = [...map.values()]
      .sort((a, b) => b.views - a.views)
      .slice(0, limit)
      .map(e => ({
        fileName: e.fileName,
        views: e.views,
        propertyCounts: [...e.byProperty.entries()]
          .map(([address, count]) => ({ address, count }))
          .sort((a, b) => b.count - a.count)
      }));
  }

  private buildTopAnnouncements(limit = 200): void {
    const map = new Map<string, { announcementId: string; title: string; views: number; byProperty: Map<string, number> }>();

    for (const stat of this.stats || []) {
      const address = stat?.address || '—';
      const items = stat?.interactionsDetailed || [];
      for (const it of items) {
        if (it?.type !== 'announcement-view') continue;

        const id = (it?.metadata?.announcementId || it?.metadata?.id || '').toString().trim() || 'unknown';
        const title = (it?.metadata?.announcementTitle || it?.metadata?.title || id).toString().trim();

        if (!map.has(id)) {
          map.set(id, { announcementId: id, title, views: 0, byProperty: new Map<string, number>() });
        }
        const entry = map.get(id)!;
        entry.views += 1;
        entry.byProperty.set(address, (entry.byProperty.get(address) || 0) + 1);
        if (entry.title === entry.announcementId && title && title !== id) entry.title = title;
      }
    }

    this.topAnnouncements = [...map.values()]
      .sort((a, b) => b.views - a.views)
      .slice(0, limit)
      .map(e => ({
        announcementId: e.announcementId,
        title: e.title,
        views: e.views,
        propertyCounts: [...e.byProperty.entries()]
          .map(([address, count]) => ({ address, count }))
          .sort((a, b) => b.count - a.count)
      }));
  }

  propertiesTooltip(items: Array<{ address: string; count: number }>, max = 10): string {
    const shown = items.slice(0, max).map(i => `${i.address} (${i.count})`);
    const extra = items.length > max ? ` +${items.length - max}` : '';
    return shown.join('\n') + extra;
  }

  propertiesInline(items: Array<{ address: string; count: number }>, max = 2): string {
    const shown = items.slice(0, max).map(i => `${i.address} (${i.count})`);
    const extra = items.length > max ? ` +${items.length - max}` : '';
    return shown.join(' · ') + extra;
  }

  clearPropertySearch(): void {
    this.propertySearch = '';
  }

  formatDateTimeByLang(date: Date | string | null | undefined): string {
    if (!date) return this.translate.instant('DASHBOARD.NA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString(this.translate.currentLang, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private loadSubscriberPropertiesPage(): void {
    this.notifLoading = true;

    this.propertyService.getPushSubscriberProperties({
      page: this.notifPageIndex,
      pageSize: this.notifPageSize,
      search: this.notifSearch?.trim() || undefined,
      sortField: this.notifSortField,
      sortOrder: this.notifSortOrder
    }).subscribe({
      next: (res) => {
        this.subscriberPropertiesPage = res.items || [];
        this.notifTotal = res.total || 0;
        this.notifLoading = false;
      },
      error: () => {
        this.notifLoading = false;
      }
    });
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;

    // Lazy-load notifications table only when user opens the Notifications tab
    if (index === 1) {
      this.loadSubscriberPropertiesPage();
    }
  }

  onNotifQueryParamsChange(params: any): void {
    const { pageIndex, pageSize, sort } = params;

    this.notifPageIndex = pageIndex;
    this.notifPageSize = pageSize;

    const activeSort = (sort || []).find((s: any) => s.value === 'ascend' || s.value === 'descend');
    if (activeSort) {
      this.notifSortField = activeSort.key === 'address' ? 'address' : 'pushSubscribers';
      this.notifSortOrder = activeSort.value;
    } else {
      this.notifSortField = 'pushSubscribers';
      this.notifSortOrder = 'descend';
    }

    this.loadSubscriberPropertiesPage();
  }

  onNotifSearchChange(): void {
    if (this.notifSearchDebounceTimer) {
      clearTimeout(this.notifSearchDebounceTimer);
    }
    this.notifSearchDebounceTimer = setTimeout(() => {
      this.notifPageIndex = 1;
      this.loadSubscriberPropertiesPage();
    }, 300);
  }

  clearNotifSearch(): void {
    this.notifSearch = '';
    this.notifPageIndex = 1;
    this.loadSubscriberPropertiesPage();
  }
}
